// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { tekUcus } from '@/api/tekUcus';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useBrand } from '@/hooks/useBrand';

interface FirstRunState {
  readonly hasLicense: boolean;
  readonly licenseExpired: boolean;
  readonly needsOnboarding: boolean;
  readonly hasInstalledApps: boolean;
  readonly suggestedRoute: string | null;
}

const ALLOW_PATHS = [
  '/onboarding',
  '/marketplace/checkout',
  '/marketplace/success',
  '/admin/apps',
  '/settings/companies',
  '/profile',
];

/**
 * Login sonrası kullanıcının durumuna göre yönlendirir:
 *  - needsOnboarding: /onboarding/wizard
 *  - lisans yok: /marketplace/checkout
 *  - SmartERP / temel app yüklü değil: /admin/apps
 * Allow-list path'lerde kullanıcı zaten ilgili adımdaysa karışmaz.
 */
export function FirstRunGuard({ children }: { readonly children: ReactNode }): ReactElement {
  const user = useAuthStore((s) => s.user);
  const brand = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  // Branded ürünlerde (Yıkamatik, SmartŞarj vb.) onboarding guard pas geçilir —
  // bu subdomain'lerde app preset auto-install çalıştığı varsayılır; kullanıcı
  // her sayfa tıklamasında /admin/apps'e yönlendirilmemeli (önceki titreme bug'ının kaynağı).
  const isBrandedProduct = !!brand.subdomain && brand.subdomain !== 'elpocore';

  const userId = user?.id;
  useEffect(() => {
    if (!userId || isBrandedProduct) { setChecked(true); return; }

    let iptal = false;
    tekUcus('onboarding/state', () => apiClient.get<FirstRunState>('/api/onboarding/state'))
      .then((res) => {
        if (iptal) return;

        const onAllowedPath = ALLOW_PATHS.some((p) => location.pathname.startsWith(p));
        if (onAllowedPath) return;

        if (res.data.suggestedRoute && location.pathname !== res.data.suggestedRoute) {
          navigate(res.data.suggestedRoute, { replace: true });
        }
      })
      .catch(() => { /* Sessizce yut — guard fail-open olsun */ })
      .finally(() => { if (!iptal) setChecked(true); });

    return () => { iptal = true; };
  }, [userId, location.pathname, isBrandedProduct, navigate]);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  return <>{children}</>;
}
